import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import VisibilityIcon from "@mui/icons-material/Visibility";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

import { ListComponent } from "@auto/widgets/ListComponent";
import { rest } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

const DEFAULT_PAGE_SIZE = 50;

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.replace("T", " ").slice(0, 19);
  }

  try {
    return new Date(value).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return String(value);
  }
};

const formatAdditionalValue = (value, displayType) => {
  if (value === null || value === undefined || value === "") return "";
  if (displayType === "datetime") return formatDateTime(value);
  if (displayType === "date" || displayType === "time") return String(value);
  if (displayType === "boolean") return value ? "Yes" : "No";
  if (displayType === "json") {
    try { return typeof value === "string" ? value : JSON.stringify(value); }
    catch { return String(value); }
  }
  return String(value);
};

const getField = (row, camelName, snakeName) =>
  row?.[camelName] ?? row?.[snakeName] ?? "";

const fullName = (row) => [
  getField(row, "firstName", "first_name"),
  getField(row, "lastName", "last_name")
].map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const escapeRsqlRegexValue = (value) => String(value || "")
  .trim()
  .replace(/\\/g, "\\\\")
  .replace(/"/g, '\\"')
  .replace(/[.*+?^{}$()|[\]\\]/g, "\\$&");

const buildSearchQuery = (search) => {
  const raw = String(search || "").trim();
  const regex = escapeRsqlRegexValue(raw);

  if (!regex) {
    return "";
  }

  const parts = [
    `username=re="(?i).*${regex}.*"`,
    `firstName=re="(?i).*${regex}.*"`,
    `lastName=re="(?i).*${regex}.*"`,
    `nationalId=re="(?i).*${regex}.*"`
  ];

  if (/^\d+$/.test(raw)) {
    parts.push(`gid==${Number(raw)}`);
  }

  return parts.join(",");
};

const buildSort = (sort) => {
  const out = [];

  for (const item of Array.isArray(sort) ? sort : []) {
    const selector = item?.column?.sortSelector || item?.field;
    const direction = item?.direction;

    if (!selector || (direction !== "asc" && direction !== "desc")) {
      continue;
    }

    out.push({ selector, direction });
  }

  return out;
};


export const setOperatorUserEnabled = async (gid, enabled) => {
  if (gid === null || gid === undefined) {
    throw new Error("operator_user.gid is required");
  }

  return await rest.request(
    getServiceUri()
      + `operator-user/${Number(gid)}/${enabled ? "enable" : "disable"}`,
    "POST",
    null
  );
};

const normalizeRow = (row) => ({
  ...row,
  gid: getField(row, "gid", "gid"),
  fullName: fullName(row),
  username: getField(row, "username", "username"),
  nationalId: getField(row, "nationalId", "national_id"),
  status: getField(row, "status", "status"),
  createdAtFormatted: formatDateTime(getField(row, "createdAt", "created_at")),
  updatedAtFormatted: formatDateTime(getField(row, "updatedAt", "updated_at")),
  approvalRequestGid: getField(row, "approvalRequestGid", "approval_request_gid"),
  communeGid: formatAdditionalValue(
    getField(row, "communeGid", "commune_gid"),
    "text"
  ),
  departmentGid: formatAdditionalValue(
    getField(row, "departmentGid", "department_gid"),
    "text"
  ),
  operatorRole: formatAdditionalValue(
    getField(row, "operatorRole", "operator_role"),
    "text"
  ),
  votingCenterGid: formatAdditionalValue(
    getField(row, "votingCenterGid", "voting_center_gid"),
    "text"
  ),
});

export const OperatorUserListWidget = ({
  gui,
  enrichRows,
  extraColumns = [],
  renderActions
}) => {
  const [refreshKey, setRefreshKey] = React.useState(0);

  const changeEnabled = React.useCallback(async (row, enabled) => {
    await setOperatorUserEnabled(row.gid, enabled);
    setRefreshKey((value) => value + 1);
  }, []);

  const projection = React.useMemo(() => ({
    storageKey: "security.operator-user",
    columns: [
      { title: "person.lastName", field: "fullName", sortSelector: "lastName" },
      { title: "user.username", field: "username", sortSelector: "username" },
      { title: "person.nationalId", field: "nationalId", sortSelector: "nationalId" },
      { title: "common.status", field: "status", sortSelector: "status" },
      { title: "user.createdAt", field: "createdAtFormatted", sortSelector: "createdAt" },
      { title: "user.updatedAt", field: "updatedAtFormatted", sortSelector: "updatedAt" },
      { title: "common.communeGid", field: "communeGid", sortSelector: "communeGid" },
      { title: "common.departmentGid", field: "departmentGid", sortSelector: "departmentGid" },
      { title: "common.operatorRole", field: "operatorRole", sortSelector: "operatorRole" },
      { title: "common.votingCenterGid", field: "votingCenterGid", sortSelector: "votingCenterGid" },
      ...extraColumns,
      { title: "Actions", field: "__actions", sorting: false, filtering: false }
    ],
    actions: (row, currentGui) => (
      <>
        <Tooltip title="View">
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              currentGui.goTo(`/operator-user/${row.gid}`);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {row.approvalRequestGid ? (
          <Tooltip title="View enrollment request">
            <IconButton
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                currentGui.goTo(`/operator-account-request/${row.approvalRequestGid}`);
              }}
            >
              <AssignmentTurnedInOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        {can(currentGui, "MANAGE_OPERATOR_USER") ? (
          String(row.status || "").toUpperCase() === "DISABLED" ? (
            <Tooltip title="Enable">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  changeEnabled(row, true).catch((error) => {
                    console.error("Failed to enable operator user", error);
                  });
                }}
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Disable">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  changeEnabled(row, false).catch((error) => {
                    console.error("Failed to disable operator user", error);
                  });
                }}
              >
                <BlockOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        ) : null}
        {renderActions ? renderActions(row, currentGui) : null}
      </>
    ),
    fetch: async (query) => {
      const page = Number.isFinite(query?.page) ? query.page : 0;
      const pageSize = Number.isFinite(query?.pageSize) ? query.pageSize : DEFAULT_PAGE_SIZE;
      const search = buildSearchQuery(query?.search);
      const payload = {
        columns: [],
        query: search || null,
        limit: pageSize,
        offset: page * pageSize,
        sort: buildSort(query?.sort)
      };

      void refreshKey;

      const [rows, totalCount] = await Promise.all([
        rest.request(getServiceUri() + "operator-user/search", "POST", payload),
        rest.request(getServiceUri() + "operator-user/count", "POST", {
          columns: [], query: payload.query, limit: null, offset: null
        })
      ]);

      let data = (rows || []).map(normalizeRow);
      if (enrichRows) {
        data = await enrichRows(data, gui);
      }

      return { data, totalCount, page };
    }
  }), [changeEnabled, enrichRows, extraColumns, gui, refreshKey, renderActions]);

  return <ListComponent gui={gui} projection={projection} />;
};

export default OperatorUserListWidget;
